import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CheckCircle, CreditCard, Lock, Sparkle } from "@phosphor-icons/react";

import { API, USER_ID } from "../App";
import { useLanguage } from "../context/LanguageContext";

const copy = {
  fr: {
    kicker: "Achat sécurisé",
    title: "Choisis ton accès myfitbody.fr",
    subtitle: "Active ton accompagnement nutrition, entraînement et suivi complet avec un paiement sécurisé Stripe.",
    secure: "Paiement 100% sécurisé",
    secureBody: "Ton règlement passe par Stripe dans un checkout sécurisé et fiable.",
    instant: "Accès immédiat",
    instantBody: "Dès validation du paiement, ton accès est prêt et ton suivi peut commencer.",
    included: "Ce que tu débloques",
    includedList: [
      "Tableau de bord nutrition + entraînement + progression",
      "Suivi quotidien clair et premium",
      "Expérience myfitbody.fr plus complète et plus fluide",
    ],
    cta: "Acheter maintenant",
    processing: "Vérification du paiement en cours...",
    paid: "Paiement confirmé ! Ton accès est bien activé.",
    pending: "Ton paiement est encore en cours de traitement.",
    failed: "Le paiement n'a pas encore été confirmé.",
    popular: "Le plus choisi",
  },
  en: {
    kicker: "Secure checkout",
    title: "Choose your myfitbody.fr access",
    subtitle: "Unlock your nutrition, training, and progress experience with secure Stripe payment.",
    secure: "100% secure payment",
    secureBody: "Your payment is processed through Stripe in a secure hosted checkout.",
    instant: "Instant access",
    instantBody: "As soon as payment is validated, your access is ready and your tracking can begin.",
    included: "What you unlock",
    includedList: [
      "Nutrition + training + progress dashboard",
      "Clear premium daily tracking",
      "A more complete myfitbody.fr experience",
    ],
    cta: "Buy now",
    processing: "Checking payment status...",
    paid: "Payment confirmed! Your access is active.",
    pending: "Your payment is still being processed.",
    failed: "Payment has not been confirmed yet.",
    popular: "Most popular",
  },
};

const formatPrice = (language, amount, currency) =>
  new Intl.NumberFormat(language === "fr" ? "fr-FR" : "en-US", {
    style: "currency",
    currency: (currency || "EUR").toUpperCase(),
  }).format(amount);

export default function CheckoutPage() {
  const { language } = useLanguage();
  const t = copy[language];
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState("boost");
  const [loading, setLoading] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState("");
  const [paymentState, setPaymentState] = useState("idle");
  const [packagesError, setPackagesError] = useState("");
  const [packagesLoading, setPackagesLoading] = useState(true);

  const sessionId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("session_id");
  }, []);

  useEffect(() => {
    const fetchPackages = async () => {
      setPackagesLoading(true);
      setPackagesError("");
      try {
        const response = await axios.get(`${API}/payments/packages`, { params: { language } });
        setPackages(response.data);
      } catch (error) {
        setPackagesError(language === "fr" ? "Impossible de charger les offres pour le moment." : "Unable to load offers right now.");
      } finally {
        setPackagesLoading(false);
      }
    };
    fetchPackages();
  }, [language]);

  useEffect(() => {
    if (!sessionId) return;

    const poll = async () => {
      setPaymentState("pending");
      setPaymentMessage(t.processing);

      for (let attempt = 0; attempt < 6; attempt += 1) {
        try {
          const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
          if (response.data.payment_status === "paid") {
            setPaymentState("paid");
            setPaymentMessage(t.paid);
            return;
          }
          if (response.data.status === "expired") {
            setPaymentState("failed");
            setPaymentMessage(t.failed);
            return;
          }
        } catch (error) {
          setPaymentState("failed");
          setPaymentMessage(t.failed);
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      setPaymentState("pending");
      setPaymentMessage(t.pending);
    };

    poll();
  }, [sessionId, t.failed, t.paid, t.pending, t.processing]);

  const startCheckout = async () => {
    setLoading(true);
    setPaymentMessage("");
    try {
      const response = await axios.post(`${API}/payments/checkout/session`, {
        package_id: selectedPackage,
        origin_url: window.location.origin,
        user_id: USER_ID,
        language,
      });
      window.location.href = response.data.url;
    } catch (error) {
      setPaymentState("failed");
      setPaymentMessage(language === "fr" ? "Impossible d’ouvrir le paiement pour le moment." : "Unable to open checkout right now.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 animate-fadeIn" data-testid="checkout-page">
      <div className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
        <section className="rounded-[2rem] bg-[#0A0A0A] text-white p-6 md:p-8 shadow-[0_24px_60px_rgba(0,0,0,0.18)]" data-testid="checkout-summary-card">
          <p className="landing-section-kicker text-white/50" data-testid="checkout-kicker">{t.kicker}</p>
          <h1 className="landing-section-title text-white mt-4" data-testid="checkout-title">{t.title}</h1>
          <p className="mt-4 text-sm md:text-base leading-7 text-white/70" data-testid="checkout-subtitle">{t.subtitle}</p>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <div className="rounded-[1.5rem] border border-white/10 bg-white/5 p-4" data-testid="checkout-secure-box">
              <Lock size={22} className="text-[#FF6B35]" weight="fill" />
              <p className="mt-3 font-semibold text-white">{t.secure}</p>
              <p className="mt-2 text-sm leading-6 text-white/65">{t.secureBody}</p>
            </div>
            <div className="rounded-[1.5rem] border border-white/10 bg-white/5 p-4" data-testid="checkout-instant-box">
              <Sparkle size={22} className="text-[#FF6B35]" weight="fill" />
              <p className="mt-3 font-semibold text-white">{t.instant}</p>
              <p className="mt-2 text-sm leading-6 text-white/65">{t.instantBody}</p>
            </div>
          </div>

          <div className="mt-8 rounded-[1.5rem] border border-white/10 bg-white/5 p-5" data-testid="checkout-included-card">
            <p className="font-semibold text-white">{t.included}</p>
            <div className="mt-4 space-y-3">
              {t.includedList.map((item, index) => (
                <div className="flex items-start gap-3" data-testid={`checkout-included-${index}`} key={item}>
                  <CheckCircle size={18} className="mt-1 text-[#FF6B35]" weight="fill" />
                  <p className="text-sm leading-6 text-white/70">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="rounded-[2rem] bg-white border border-black/8 p-6 md:p-8 shadow-[0_18px_40px_rgba(15,23,42,0.06)]" data-testid="checkout-offers-card">
          {paymentMessage ? (
            <div className={`mb-5 rounded-[1.25rem] px-4 py-3 text-sm font-semibold ${paymentState === "paid" ? "bg-emerald-50 text-emerald-700" : paymentState === "failed" ? "bg-rose-50 text-rose-700" : "bg-sky-50 text-sky-700"}`} data-testid="checkout-payment-status">
              {paymentMessage}
            </div>
          ) : null}

          {packagesError ? (
            <div className="mb-5 rounded-[1.25rem] bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700" data-testid="checkout-packages-error">
              {packagesError}
            </div>
          ) : null}

          <div className="space-y-4">
            {packagesLoading ? (
              <div className="rounded-[1.5rem] border border-dashed border-black/10 bg-[#FAFAFA] px-4 py-6 text-sm text-gray-500" data-testid="checkout-packages-loading">
                {language === "fr" ? "Chargement des offres..." : "Loading offers..."}
              </div>
            ) : null}
            {packages.map((item) => {
              const active = selectedPackage === item.id;
              return (
                <button
                  className={`w-full rounded-[1.5rem] border p-5 text-left transition-all ${active ? "border-[#FF6B35] bg-[#FFF0EA]" : "border-black/8 bg-[#FAFAFA] hover:border-[#FF6B35]/40"}`}
                  data-testid={`checkout-package-${item.id}`}
                  key={item.id}
                  onClick={() => setSelectedPackage(item.id)}
                  type="button"
                >
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <p className="font-display text-3xl uppercase text-[#0A0A0A]">{item.label}</p>
                      {item.is_popular ? (
                        <span className="mt-2 inline-flex rounded-full bg-[#0A0A0A] px-3 py-1 text-xs font-bold uppercase tracking-[0.28em] text-white" data-testid={`checkout-package-badge-${item.id}`}>
                          {t.popular}
                        </span>
                      ) : null}
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-400 line-through">{formatPrice(language, item.old_price, item.currency)}</p>
                      <p className="font-display text-4xl uppercase text-[#FF6B35]">{formatPrice(language, item.price, item.currency)}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <button className="landing-primary-button mt-6 w-full" data-testid="checkout-start-button" onClick={startCheckout} type="button" disabled={loading}>
            <CreditCard size={18} weight="fill" />
            {loading ? "..." : t.cta}
          </button>
        </section>
      </div>
    </div>
  );
}